import React, { useEffect } from "react";

import { css } from "@compiled/react";
import { UniformSlot } from "@uniformdev/canvas-react";
import { useUniformCurrentComposition } from "@uniformdev/canvas-react";

import { media } from "@atlaskit/primitives";

import { wacToken } from "@/libraries/design/foundations";
import {
    WCL_GRID_MAX_WIDTH,
    WCL_GRID_PADDING_LG
} from "@/libraries/design/foundations/grid/src/base";
import {
    updateStickyNavOffset,
    SECTION_DIVIDER_HEIGHTS,
    SectionDividerType
} from "@/libraries/design/utils";
import { isDarkColor } from "@/utils/colors/colors";
import {
    checkFeatureFlagGate,
    featureFlags as featureFlagsManifest
} from "@/utils/feature-flags";
import { useScrollTracking } from "@/utils/scroll-tracking";

import { spaceTokenNumberValue } from "../../../utils";

export type UniformContainerProps = {
    spacing?: ResponsiveSpacingOptions;
    horizontalAlignment?: UniformContainerHorizontalAlignment;
    verticalAlignment?: UniformContainerVerticalAlignment;
    orientation?: ResponsiveOrientationOptions;
    gapv2?: ResponsiveGapOptions;
    hat?: SectionDividerType;
    pants?: SectionDividerType;
    backgroundColor?: string;
    extendBackgroundColor?: boolean;
    sticky?: boolean;
    responsiveHiding?: ViewportSize[];
    overflow?: "visible" | "hidden";
    label?: string;
    tagType?: "div" | "aside" | "section" | "article";
};

type ViewportSize = "mobile" | "tablet" | "desktop";

type UniformContainerHorizontalAlignment =
    | "default"
    | "center"
    | "start"
    | "end";
type UniformContainerVerticalAlignment = "default" | "center" | "start" | "end";

type UniformContainerOrientation = "horizontal" | "vertical";

export type ResponsiveOrientationOptions = Record<
    ViewportSize,
    UniformContainerOrientation
>;

type spacingOptions = {
    marginTop?: spaceTokenNumberValue;
    marginBottom?: spaceTokenNumberValue;
    marginLeft?: spaceTokenNumberValue;
    marginRight?: spaceTokenNumberValue;
    paddingTop?: spaceTokenNumberValue;
    paddingBottom?: spaceTokenNumberValue;
    paddingLeft?: spaceTokenNumberValue;
    paddingRight?: spaceTokenNumberValue;
};

type ResponsiveSpacingOptions = Record<ViewportSize, spacingOptions>;

export type ResponsiveGapOptions = Record<ViewportSize, spaceTokenNumberValue>;

const UniformContainer: React.FC<UniformContainerProps> = ({
    spacing,
    horizontalAlignment = "default",
    verticalAlignment = "default",
    orientation = {
        mobile: "vertical"
    },
    gapv2 = {
        mobile: "0"
    },
    hat,
    pants,
    backgroundColor,
    extendBackgroundColor,
    sticky = false,
    responsiveHiding,
    overflow = "visible",
    label,
    tagType: Component = "div"
    // eslint-disable-next-line complexity
}) => {
    const composition = useUniformCurrentComposition();
    const compositionId = composition?.data?._id;
    const tracker = useScrollTracking(label ?? "", {
        dwellTimeEnabled: true,
        additionalAttributes: {
            compositionId
        }
    });
    const spacingObject: ResponsiveSpacingOptions = {
        mobile: {
            marginTop: spacing?.mobile?.marginTop ?? "0",
            marginBottom: spacing?.mobile?.marginBottom ?? "0",
            marginLeft: spacing?.mobile?.marginLeft ?? "0",
            marginRight: spacing?.mobile?.marginRight ?? "0",
            paddingTop: spacing?.mobile?.paddingTop ?? "0",
            paddingBottom: spacing?.mobile?.paddingBottom ?? "0",
            paddingLeft: spacing?.mobile?.paddingLeft ?? "0",
            paddingRight: spacing?.mobile?.paddingRight ?? "0"
        },
        tablet: {
            marginTop:
                spacing?.tablet?.marginTop ?? spacing?.mobile?.marginTop ?? "0",
            marginBottom:
                spacing?.tablet?.marginBottom ??
                spacing?.mobile?.marginBottom ??
                "0",
            marginLeft:
                spacing?.tablet?.marginLeft ??
                spacing?.mobile?.marginLeft ??
                "0",
            marginRight:
                spacing?.tablet?.marginRight ??
                spacing?.mobile?.marginRight ??
                "0",
            paddingTop:
                spacing?.tablet?.paddingTop ??
                spacing?.mobile?.paddingTop ??
                "0",
            paddingBottom:
                spacing?.tablet?.paddingBottom ??
                spacing?.mobile?.paddingBottom ??
                "0",
            paddingLeft:
                spacing?.tablet?.paddingLeft ??
                spacing?.mobile?.paddingLeft ??
                "0",
            paddingRight:
                spacing?.tablet?.paddingRight ??
                spacing?.mobile?.paddingRight ??
                "0"
        },
        desktop: {
            marginTop:
                spacing?.desktop?.marginTop ??
                spacing?.tablet?.marginTop ??
                spacing?.mobile?.marginTop ??
                "0",
            marginBottom:
                spacing?.desktop?.marginBottom ??
                spacing?.tablet?.marginBottom ??
                spacing?.mobile?.marginBottom ??
                "0",
            marginLeft:
                spacing?.desktop?.marginLeft ??
                spacing?.tablet?.marginLeft ??
                spacing?.mobile?.marginLeft ??
                "0",
            marginRight:
                spacing?.desktop?.marginRight ??
                spacing?.tablet?.marginRight ??
                spacing?.mobile?.marginRight ??
                "0",
            paddingTop:
                spacing?.desktop?.paddingTop ??
                spacing?.tablet?.paddingTop ??
                spacing?.mobile?.paddingTop ??
                "0",
            paddingBottom:
                spacing?.desktop?.paddingBottom ??
                spacing?.tablet?.paddingBottom ??
                spacing?.mobile?.paddingBottom ??
                "0",
            paddingLeft:
                spacing?.desktop?.paddingLeft ??
                spacing?.tablet?.paddingLeft ??
                spacing?.mobile?.paddingLeft ??
                "0",
            paddingRight:
                spacing?.desktop?.paddingRight ??
                spacing?.tablet?.paddingRight ??
                spacing?.mobile?.paddingRight ??
                "0"
        }
    };
    const marginStyles = css({
        marginBlockStart:
            "var(--ds-space-" + spacingObject.mobile.marginTop + ")",
        marginBlockEnd:
            "var(--ds-space-" + spacingObject.mobile.marginBottom + ")",
        marginInlineStart:
            "var(--ds-space-" + spacingObject.mobile.marginLeft + ")",
        marginInlineEnd:
            "var(--ds-space-" + spacingObject.mobile.marginRight + ")",
        [media.above.sm]: {
            marginBlockStart:
                "var(--ds-space-" + spacingObject.tablet.marginTop + ")",
            marginBlockEnd:
                "var(--ds-space-" + spacingObject.tablet.marginBottom + ")",
            marginInlineStart:
                "var(--ds-space-" + spacingObject.tablet.marginLeft + ")",
            marginInlineEnd:
                "var(--ds-space-" + spacingObject.tablet.marginRight + ")"
        },
        [media.above.md]: {
            marginBlockStart:
                "var(--ds-space-" + spacingObject.desktop.marginTop + ")",
            marginBlockEnd:
                "var(--ds-space-" + spacingObject.desktop.marginBottom + ")",
            marginInlineStart:
                "var(--ds-space-" + spacingObject.desktop.marginLeft + ")",
            marginInlineEnd:
                "var(--ds-space-" + spacingObject.desktop.marginRight + ")"
        }
    });
    const paddingStyles = css({
        paddingBlockStart:
            "var(--ds-space-" + spacingObject.mobile.paddingTop + ")",
        paddingBlockEnd:
            "var(--ds-space-" + spacingObject.mobile.paddingBottom + ")",
        paddingInlineStart:
            "var(--ds-space-" + spacingObject.mobile.paddingLeft + ")",
        paddingInlineEnd:
            "var(--ds-space-" + spacingObject.mobile.paddingRight + ")",
        [media.above.sm]: {
            paddingBlockStart:
                "var(--ds-space-" + spacingObject.tablet.paddingTop + ")",
            paddingBlockEnd:
                "var(--ds-space-" + spacingObject.tablet.paddingBottom + ")",
            paddingInlineStart:
                "var(--ds-space-" + spacingObject.tablet.paddingLeft + ")",
            paddingInlineEnd:
                "var(--ds-space-" + spacingObject.tablet.paddingRight + ")"
        },
        [media.above.md]: {
            paddingBlockStart:
                "var(--ds-space-" + spacingObject.desktop.paddingTop + ")",
            paddingBlockEnd:
                "var(--ds-space-" + spacingObject.desktop.paddingBottom + ")",
            paddingInlineStart:
                "var(--ds-space-" + spacingObject.desktop.paddingLeft + ")",
            paddingInlineEnd:
                "var(--ds-space-" + spacingObject.desktop.paddingRight + ")"
        }
    });

    useEffect(() => {
        updateStickyNavOffset();
    }, []);

    const hiddenMobileStyle = css({
        [media.below.sm]: {
            display: "none"
        }
    });
    const hiddenTabletStyle = css({
        [media.only.sm]: {
            display: "none"
        }
    });
    const hiddenDesktopStyle = css({
        [media.above.md]: {
            display: "none"
        }
    });

    // determine text color based on background color luminance
    const getTextColor = (bgColor?: string): string | undefined => {
        if (!bgColor) return undefined;

        try {
            // white text for dark backgrounds, dark text for light backgrounds
            return isDarkColor(bgColor)
                ? wacToken("color.neutral.light0")
                : wacToken("color.primary.black");
        } catch (error) {
            // invalid color format, return undefined to use default
            console.error("Invalid backgroundColor format:", bgColor, error);
            return undefined;
        }
    };

    const textColor = getTextColor(backgroundColor);

    const colorStyle = css({
        "--dxp-container-background-color": backgroundColor,
        "--dxp-container-text-color": textColor
    });

    const gapObject: ResponsiveGapOptions = {
        mobile: (gapv2 as ResponsiveGapOptions).mobile ?? "0",
        tablet:
            (gapv2 as ResponsiveGapOptions)?.tablet ??
            (gapv2 as ResponsiveGapOptions).mobile ??
            "0",
        desktop:
            (gapv2 as ResponsiveGapOptions)?.desktop ??
            (gapv2 as ResponsiveGapOptions)?.tablet ??
            (gapv2 as ResponsiveGapOptions).mobile ??
            "0"
    };

    const innerStyle = css({
        background: "var(--dxp-container-background-color, transparent)",
        width: "100%",
        display: "flex",
        gap: "var(--ds-space-" + gapObject.mobile + ")",
        [media.above.sm]: {
            gap: "var(--ds-space-" + gapObject.tablet + ")"
        },
        [media.above.md]: {
            gap: "var(--ds-space-" + gapObject.desktop + ")"
        }
    });

    const innerStyleFullHeight = css({
        height: "100%"
    });

    const orientationObject: ResponsiveOrientationOptions = {
        mobile:
            (orientation as ResponsiveOrientationOptions).mobile || "vertical",
        tablet:
            (orientation as ResponsiveOrientationOptions)?.tablet ||
            (orientation as ResponsiveOrientationOptions).mobile ||
            "vertical",
        desktop:
            (orientation as ResponsiveOrientationOptions)?.desktop ||
            (orientation as ResponsiveOrientationOptions)?.tablet ||
            (orientation as ResponsiveOrientationOptions).mobile ||
            "vertical"
    };
    const orientationStyle = css({
        display: "flex",
        flexDirection:
            orientationObject.mobile === "horizontal" ? "row" : "column",
        [media.above.sm]: {
            flexDirection:
                orientationObject.tablet === "horizontal" ? "row" : "column"
        },
        [media.above.md]: {
            flexDirection:
                orientationObject.desktop === "horizontal" ? "row" : "column"
        }
    });

    const verticalAlignmentValue =
        verticalAlignment === "center"
            ? "center"
            : verticalAlignment === "end"
            ? "flex-end"
            : "flex-start";
    const horizontalAlignmentValue =
        horizontalAlignment === "center"
            ? "center"
            : horizontalAlignment === "start"
            ? "flex-start"
            : horizontalAlignment === "end"
            ? "flex-end"
            : "initial";

    const hiddenOnMobile = responsiveHiding?.includes("mobile");
    const hiddenOnTablet = responsiveHiding?.includes("tablet");
    const hiddenOnDesktop = responsiveHiding?.includes("desktop");

    const overflowStyle = css({
        overflow: overflow
    });

    const outerHeightFullStyle = css({
        height: "100%"
    });

    const responsiveSectionDividerHeightsStyles = css({
        "--hat-height": hat ? `${SECTION_DIVIDER_HEIGHTS.mobile}px` : "0px",
        "--pants-height": pants ? `${SECTION_DIVIDER_HEIGHTS.mobile}px` : "0px",
        [media.above.sm]: {
            "--hat-height": hat ? `${SECTION_DIVIDER_HEIGHTS.tablet}px` : "0px",
            "--pants-height": pants
                ? `${SECTION_DIVIDER_HEIGHTS.tablet}px`
                : "0px"
        },
        [media.above.md]: {
            "--hat-height": hat
                ? `${SECTION_DIVIDER_HEIGHTS.desktop}px`
                : "0px",
            "--pants-height": pants
                ? `${SECTION_DIVIDER_HEIGHTS.desktop}px`
                : "0px"
        }
    });

    const extendedBackgroundColorComponent = (
        <div
            css={[
                css({
                    position: "absolute",
                    width: "100vw",
                    height: "calc(100% - var(--hat-height) - var(--pants-height))",
                    [media.above.sm]: {
                        width: `calc(${WCL_GRID_MAX_WIDTH}px + (${WCL_GRID_PADDING_LG}px * 2))`, // for when the grid width is stopped at its max with
                        maxWidth: `min(calc(100% - ${WCL_GRID_PADDING_LG}px), calc(${WCL_GRID_MAX_WIDTH}px + 80px))` // for when the grid width is less than its max width, and to have background color max width at grid max width + 80px
                    },
                    insetInlineStart: "50%",
                    transform: "translateX(-50%)",
                    background:
                        "var(--dxp-container-background-color, transparent)"
                }),
                hat && hatCommonStyle,
                hat && extendedBackgroundColorHatStyle,
                hat && hat === "high-right"
                    ? hatHighRight
                    : hat === "high-left"
                    ? hatHighLeft
                    : hatPolygon,
                pants && pantsCommonStyle,
                pants && extendedBackgroundColorPantsStyle,
                pants && hat && extendedBackgroundColorPantsWithHatStyle,
                pants && pants === "high-right"
                    ? pantsHighRight
                    : pants === "high-left"
                    ? pantsHighLeft
                    : pantsPolygon
            ]}
        />
    );

    return (
        <Component
            ref={
                checkFeatureFlagGate(
                    featureFlagsManifest.gwp7603UniformPageDwellTime
                ) && label
                    ? (tracker?.ref as React.LegacyRef<HTMLDivElement>)
                    : null
            }
            css={[
                responsiveSectionDividerHeightsStyles,
                marginStyles,
                colorStyle,
                verticalAlignment === "end" && outerHeightFullStyle,
                hat && !extendBackgroundColor && hatCommonStyle,
                hat && !extendBackgroundColor && hat === "high-right"
                    ? hatHighRight
                    : hat === "high-left"
                    ? hatHighLeft
                    : hatPolygon,
                pants && !extendBackgroundColor && pantsCommonStyle,
                pants && !extendBackgroundColor && pants === "high-right"
                    ? pantsHighRight
                    : pants === "high-left"
                    ? pantsHighLeft
                    : pantsPolygon,
                hiddenOnMobile && hiddenMobileStyle,
                hiddenOnTablet && hiddenTabletStyle,
                hiddenOnDesktop && hiddenDesktopStyle,
                overflowStyle,
                extendBackgroundColor &&
                    css({
                        position: "relative"
                    }),
                extendBackgroundColor &&
                    hat &&
                    css({ paddingBlockStart: "var(--hat-height)" }),
                extendBackgroundColor &&
                    pants &&
                    css({ paddingBlockEnd: "var(--pants-height)" })
            ]}
        >
            {extendBackgroundColor && extendedBackgroundColorComponent}
            <div
                css={[
                    innerStyle,
                    verticalAlignment === "end" && innerStyleFullHeight,
                    extendBackgroundColor &&
                        css({
                            backgroundColor: "transparent",
                            position: "relative",
                            zIndex: 1
                        }),
                    paddingStyles,
                    orientationStyle,
                    orientationObject.mobile === "horizontal" &&
                        css({
                            alignItems: verticalAlignmentValue,
                            justifyContent: horizontalAlignmentValue
                        }),
                    orientationObject.tablet === "horizontal" &&
                        css({
                            [media.above.sm]: {
                                alignItems: verticalAlignmentValue,
                                justifyContent: horizontalAlignmentValue
                            }
                        }),
                    orientationObject.desktop === "horizontal" &&
                        css({
                            [media.above.md]: {
                                alignItems: verticalAlignmentValue,
                                justifyContent: horizontalAlignmentValue
                            }
                        }),
                    orientationObject.mobile === "vertical" &&
                        css({
                            alignItems: horizontalAlignmentValue,
                            justifyContent: verticalAlignmentValue
                        }),
                    orientationObject.tablet === "vertical" &&
                        css({
                            [media.above.sm]: {
                                alignItems: horizontalAlignmentValue,
                                justifyContent: verticalAlignmentValue
                            }
                        }),
                    orientationObject.desktop === "vertical" &&
                        css({
                            [media.above.md]: {
                                alignItems: horizontalAlignmentValue,
                                justifyContent: verticalAlignmentValue
                            }
                        }),
                    sticky
                        ? css({
                              position: "sticky",
                              insetBlockStart: "var(--wae-header-height, 0px)"
                          })
                        : ""
                ]}
            >
                <UniformSlot name="component" />
            </div>
        </Component>
    );
};

const hatCommonStyle = css({
    "::before": {
        content: "",
        display: "block",
        height: "var(--hat-height)",
        background: "var(--dxp-container-background-color, transparent)"
    }
});
const extendedBackgroundColorHatStyle = css({
    "::before": {
        position: "relative",
        insetBlockStart: "calc(-1 * var(--hat-height))"
    }
});
const hatHighLeft = css({
    "::before": {
        clipPath: "polygon(0 0, 0 100.25%, 100% 100.25%)"
    }
});
const hatHighRight = css({
    "::before": {
        clipPath: "polygon(100% 0, 0 100.25%, 100% 100.25%)"
    }
});
const hatPolygon = css({
    "::before": {
        clipPath:
            "polygon(0 0, 70% 16%, 70% 66%, 100% 46%, 100% 100.25%, 0 100.25%);"
    }
});

const pantsCommonStyle = css({
    "::after": {
        content: "",
        display: "block",
        height: "var(--pants-height)",
        background: "var(--dxp-container-background-color, transparent)"
    }
});
const extendedBackgroundColorPantsStyle = css({
    "::after": {
        position: "relative",
        insetBlockStart: "100%"
    }
});
const extendedBackgroundColorPantsWithHatStyle = css({
    "::after": {
        position: "relative",
        insetBlockStart: "calc(100% - var(--pants-height))"
    }
});
const pantsHighLeft = css({
    "::after": {
        clipPath: "polygon(0 100%, 100% -0.25%, 0 -0.25%)"
    }
});
const pantsHighRight = css({
    "::after": {
        clipPath: "polygon(100% 100%, 100% -0.25%, 0 -0.25%)"
    }
});
const pantsPolygon = css({
    "::after": {
        clipPath:
            "polygon(0 -0.25%, 100% -0.25%, 100% 48%, 40% 100%, 40% 53%, 0 43%)"
    }
});

export default UniformContainer;
